import { useEffect, useState, type SyntheticEvent } from "react";
import { ChevronDown, Plus, Trash2, X } from "lucide-react";
import type { MatterDraft, SessionSummary } from "../app/ui-types";
import { recordingAuthorityStatus } from "../domain";

type RecordingSettingsProps = {
  matter: MatterDraft;
  open: boolean;
  locked: boolean;
  savedAt?: number | null;
  onChange: (matter: MatterDraft) => void;
  onClose: () => void;
  sessions?: SessionSummary[];
  sessionsReadOnly?: boolean;
  onSessionLabelChange?: (recordingId: string, label: string) => void;
};

export function RecordingSettings({
  matter,
  open,
  locked,
  savedAt = null,
  onChange,
  onClose,
  sessions = [],
  sessionsReadOnly = false,
  onSessionLabelChange
}: RecordingSettingsProps) {
  const [newSpeaker, setNewSpeaker] = useState({ name: "", role: "" });
  const [newTerm, setNewTerm] = useState("");
  const [compactDetails, setCompactDetails] = useState(false);
  const [openDisclosure, setOpenDisclosure] = useState<string | null>("matter");
  const authority = recordingAuthorityStatus(matter);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 59.999rem)");
    const update = () => setCompactDetails(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const disclosureProps = (name: string) => ({
    open: compactDetails ? openDisclosure === name : undefined,
    onToggle: (event: SyntheticEvent<HTMLDetailsElement>) => {
      if (!compactDetails) return;
      const details = event.currentTarget;
      if (details.open) setOpenDisclosure(name);
      else setOpenDisclosure((current) => (current === name ? null : current));
    }
  });

  const setField = <K extends keyof MatterDraft>(field: K, value: MatterDraft[K]) => {
    onChange({ ...matter, [field]: value });
  };

  const setAuthority = (
    fields: Array<keyof MatterDraft["authority"]>,
    value: boolean
  ) => {
    onChange({
      ...matter,
      authority: fields.reduce(
        (next, field) => ({ ...next, [field]: value }),
        matter.authority
      )
    });
  };

  const addSpeaker = () => {
    const name = newSpeaker.name.trim();
    const role = newSpeaker.role.trim();
    if (!name || !role) return;
    setField("participants", [...matter.participants, { name, role }]);
    setNewSpeaker({ name: "", role: "" });
  };

  return (
    <aside
      className={`recording-settings ${open ? "is-open" : ""}`}
      aria-label="Recording details"
      aria-hidden={!open}
    >
      <header className="recording-settings__head">
        <h2>Details</h2>
        {savedAt && (
          <span className="settings-saved" key={savedAt}>
            Saved
          </span>
        )}
        <button
          className="icon-button"
          type="button"
          onClick={onClose}
          aria-label="Close details"
          title="Close details"
        >
          <X size={17} aria-hidden="true" />
        </button>
      </header>

      <div className="recording-settings__body">
        {sessions.length > 0 && (
          <section className="settings-sessions" aria-labelledby="settings-sessions-title">
            <header>
              <h3 id="settings-sessions-title">Sessions</h3>
              <span>{sessions.length}</span>
            </header>
            <ol>
              {sessions.map((session) => (
                <li key={session.id}>
                  <span className="settings-session-number">{session.ordinal}</span>
                  <label>
                    <span className="sr-only">Session {session.ordinal} label</span>
                    <input
                      key={`${session.id}-${session.label ?? ""}`}
                      defaultValue={session.label ?? ""}
                      placeholder={`Session ${session.ordinal}`}
                      maxLength={100}
                      disabled={sessionsReadOnly}
                      onBlur={(event) =>
                        onSessionLabelChange?.(session.id, event.currentTarget.value)
                      }
                    />
                    <small>
                      {new Intl.DateTimeFormat(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit"
                      }).format(new Date(session.startedAt ?? session.createdAt))}
                    </small>
                  </label>
                  <em>{session.attention === "recovery-needed" ? "Recover" : session.status}</em>
                </li>
              ))}
            </ol>
          </section>
        )}
        <label className="field">
          <span>Recording title</span>
          <input
            className="recording-title-field"
            maxLength={180}
            value={matter.title}
            onChange={(event) => setField("title", event.target.value)}
            disabled={locked}
          />
        </label>

        <div className="recording-settings__pair">
          <label className="field">
            <span>Date</span>
            <input
              type="date"
              value={matter.date}
              onChange={(event) => setField("date", event.target.value)}
              disabled={locked}
            />
          </label>
          <label className="field">
            <span>Type</span>
            <select
              value={matter.proceeding}
              onChange={(event) =>
                setField(
                  "proceeding",
                  event.target.value as MatterDraft["proceeding"]
                )
              }
              disabled={locked}
            >
              <option>Deposition / Examination</option>
              <option>Investigation / Interview</option>
              <option>Conference</option>
              <option>Hearing</option>
            </select>
          </label>
        </div>

        <section className="recording-authority" aria-labelledby="recording-authority-title">
          <h3 id="recording-authority-title">Permission</h3>
          <label className="compact-check">
            <input
              type="checkbox"
              checked={
                matter.authority.userAttestation &&
                matter.authority.participantNotice &&
                (!authority.requiresParticipantConsent ||
                  matter.authority.participantConsent) &&
                matter.authority.confidentialityReviewed &&
                (!authority.requiresCourtApproval || matter.authority.courtApproval)
              }
              onChange={(event) =>
                setAuthority(
                  [
                    "userAttestation",
                    "participantNotice",
                    "participantConsent",
                    "confidentialityReviewed",
                    ...(authority.requiresCourtApproval
                      ? (["courtApproval"] as const)
                      : [])
                  ],
                  event.target.checked
                )
              }
              disabled={locked}
            />
            <span>
              Permission to record confirmed
              {authority.requiresCourtApproval ? ", including court approval" : ""}
            </span>
          </label>
        </section>

        <details className="settings-disclosure" {...disclosureProps("matter")}>
          <summary>
            Matter details <ChevronDown size={15} aria-hidden="true" />
          </summary>
          <div className="settings-disclosure__body">
            <label className="field">
              <span>Reference</span>
              <input
                value={matter.reference}
                onChange={(event) => setField("reference", event.target.value)}
                disabled={locked}
              />
            </label>
            <label className="field">
              <span>Venue</span>
              <input
                value={matter.venue}
                onChange={(event) => setField("venue", event.target.value)}
                disabled={locked}
              />
            </label>
            <label className="field">
              <span>Jurisdiction</span>
              <select
                value={matter.jurisdictionPack}
                onChange={(event) =>
                  setField(
                    "jurisdictionPack",
                    event.target.value as MatterDraft["jurisdictionPack"]
                  )
                }
                disabled={locked}
              >
                <option value="GLOBAL-BASE">Global base</option>
                <option value="AU-NSW">Australia · NSW</option>
                <option value="US-FED-DEPOSITION">United States · Federal</option>
              </select>
            </label>
            <div className="recording-settings__pair">
              <label className="field">
                <span>Time zone</span>
                <input
                  value={matter.timezone}
                  onChange={(event) => setField("timezone", event.target.value)}
                  disabled={locked}
                />
              </label>
              <label className="field">
                <span>Languages</span>
                <input
                  value={matter.languages.join(", ")}
                  onChange={(event) =>
                    setField(
                      "languages",
                      event.target.value
                        .split(",")
                        .map((language) => language.trim())
                        .filter(Boolean)
                    )
                  }
                  disabled={locked}
                />
              </label>
            </div>
          </div>
        </details>

        <details className="settings-disclosure" {...disclosureProps("speakers")}>
          <summary>
            Speakers <span>{matter.participants.length}</span>
            <ChevronDown size={15} aria-hidden="true" />
          </summary>
          <div className="settings-disclosure__body">
            <ul className="settings-speakers">
              {matter.participants.map((participant, index) => (
                <li key={`${participant.name}-${index}`}>
                  <span>
                    <strong>{participant.name}</strong>
                    <small>{participant.role}</small>
                  </span>
                  <button
                    className="icon-button"
                    type="button"
                    onClick={() =>
                      setField(
                        "participants",
                        matter.participants.filter(
                          (_, participantIndex) => participantIndex !== index
                        )
                      )
                    }
                    disabled={locked}
                    aria-label={`Remove ${participant.name}`}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="speaker-add">
              <label className="field">
                <span>Name</span>
                <input
                  value={newSpeaker.name}
                  onChange={(event) =>
                    setNewSpeaker((current) => ({
                      ...current,
                      name: event.target.value
                    }))
                  }
                  disabled={locked}
                />
              </label>
              <label className="field">
                <span>Role</span>
                <input
                  value={newSpeaker.role}
                  onChange={(event) =>
                    setNewSpeaker((current) => ({
                      ...current,
                      role: event.target.value
                    }))
                  }
                  disabled={locked}
                />
              </label>
              <button
                className="button button--quiet"
                type="button"
                onClick={addSpeaker}
                disabled={locked || !newSpeaker.name.trim() || !newSpeaker.role.trim()}
              >
                <Plus size={15} aria-hidden="true" /> Add speaker
              </button>
            </div>
          </div>
        </details>

        <details className="settings-disclosure" {...disclosureProps("vocabulary")}>
          <summary>
            Vocabulary <small>Optional</small> <span>{matter.glossary.length}</span>
            <ChevronDown size={15} aria-hidden="true" />
          </summary>
          <div className="settings-disclosure__body">
            <label className="field">
              <span>Names and terms (optional)</span>
              <span className="vocabulary-chips">
                {matter.glossary.map((term, index) => (
                  <span className="vocabulary-chip" key={`${term}-${index}`}>
                    {term}
                    <button
                      type="button"
                      onClick={() =>
                        setField(
                          "glossary",
                          matter.glossary.filter(
                            (_, termIndex) => termIndex !== index
                          )
                        )
                      }
                      disabled={locked}
                      aria-label={`Remove ${term}`}
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </span>
              <input
                value={newTerm}
                placeholder="Add term"
                disabled={locked}
                onChange={(event) => setNewTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  const term = newTerm.trim();
                  if (!term || matter.glossary.includes(term)) return;
                  setField("glossary", [...matter.glossary, term]);
                  setNewTerm("");
                }}
              />
            </label>
          </div>
        </details>
      </div>
    </aside>
  );
}
